import {Component, input, OnChanges} from '@angular/core'
import {EChartsOption} from 'echarts'
import {LineChart, ScatterChart} from 'echarts/charts'
import {GraphicComponent, GridComponent, TooltipComponent} from 'echarts/components'
import * as echarts from 'echarts/core'
import {EChartsType} from 'echarts/core'
// @ts-expect-error Does not work atm. Looking into it.
import lang from 'echarts/lib/i18n/langFR.js'
import {CanvasRenderer} from 'echarts/renderers'
import {NgxEchartsDirective, provideEchartsCore} from 'ngx-echarts'
import {COLOR_LIST} from '../../../application/data-types'
import {PrintableChartComponent} from '../../../common/printable-chart/printable-chart.component'
import {KalpLoan} from '../../../model/kalp-loan'

echarts.use([ScatterChart, LineChart, GraphicComponent, TooltipComponent,
  GridComponent, CanvasRenderer])
echarts.registerLocale('SV', lang)

interface IChartLoanItem extends Pick<KalpLoan, 'label' | 'amount'> {
  colorIndex: number
}

interface IChartLoan {
  changeDate: string
  amount: number
  label: string
  loans: IChartLoanItem[]
  colorIndex: number
}

/**
 * Exported for testing only.
 */
export const getFormatter = (loans: IChartLoanItem[], dateLabel: string) => {
  return () => {
    const totalAmount = loans.reduce((sum, loan) => sum + loan.amount, 0)
    const loanTexts = loans.map(loan =>
      `${loan.label}: ${loan.amount.toLocaleString('sv-SE')}`
    ).join('\n')

    return `Totalt: ${totalAmount.toLocaleString('sv-SE')}\n${dateLabel}\n${loanTexts}`
  }
}

const getSeries = (loan: IChartLoan, index: number): any => {
  // Extract color based on index to ensure consistency between dot and label.
  // Modulo (%) to prevent errors if index exceeds COLOR_LIST length.
  const assignedColor = COLOR_LIST[index % COLOR_LIST.length]

  return {
    type: 'scatter',
    itemStyle: {
      color: assignedColor,
      borderColor: assignedColor
    },
    data: [{
      value: [loan.changeDate, 2],
      label: {
        show: true,
        position: 'bottom',
        borderColor: assignedColor,
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderRadius: 2,
        align: 'center',
        lineHeight: 18,
        fontSize: 14,
        fontFamily: 'Titillium Web, sans-serif',
        padding: 7,
        distance: 30,
        formatter: getFormatter(loan.loans, loan.label),
      }
    }],
    symbol: 'circle',
    symbolSize: 12
  }
}

@Component({
  selector: 'spb-loan-overview',
  templateUrl: './loan-overview.component.html',
  styleUrls: ['./loan-overview.component.scss'],
  imports: [
    NgxEchartsDirective
  ],
  providers: [
    provideEchartsCore({echarts}),
    {provide: PrintableChartComponent, useExisting: LoanOverviewComponent}
  ]
})
export class LoanOverviewComponent extends PrintableChartComponent implements OnChanges {
  public loans = input.required<KalpLoan[]>()

  public myChart!: EChartsType

  /**
   * https://echarts.apache.org/en/option.html#series-scatter.label.formatter
   */

  public option: EChartsOption = {
    tooltip: {},
    color: COLOR_LIST,
    xAxis: {
      type: 'time',
      axisLine: {
        onZero: false,
        onZeroAxisIndex: 2
      },
      show: false
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 3,
      show: false,
      interval: 0
    },
    series: [],
    grid: {
      top: 55
    },
    graphic: [{
      type: 'image',
      style: {
        image: './assets/arrow.svg'
      },
      y: -22,
      scaleX: 2,
      scaleY: 2
    }]
  }

  public ngOnChanges() {
    const loanMap = new Map<string, IChartLoan>()
    const series: any[] = []

    // Group loans by date, preserving original color indices
    this.loans().forEach((loan, originalIndex) => {
      const existingLoan = loanMap.get(loan.changeDate)

      if (existingLoan) {
        // Add loan to existing group
        existingLoan.amount += loan.amount
        existingLoan.loans.push({
          label: loan.label,
          amount: loan.amount,
          colorIndex: originalIndex
        })
      } else {
        // Create new group, use the first loan's color index
        loanMap.set(loan.changeDate, {
          changeDate: loan.changeDate,
          amount: loan.amount,
          label: loan.changeDate,
          colorIndex: originalIndex,
          loans: [{
            label: loan.label,
            amount: loan.amount,
            colorIndex: originalIndex
          }]
        })
      }
    })

    const items = [...loanMap.values()]

    items.forEach((loan) => {
      series.push(getSeries(loan, loan.colorIndex))
    })

    // Add today's date to start today, roughly.
    series.push({
      type: 'scatter',
      data: [{value: [Date.now(), 2]}],
      symbolSize: 0
    })

    this.option.series = series
    this.option = {...this.option}
  }

  public chartInit(e: EChartsType) {
    this.myChart = e
  }

  public override print(): string {
    return this.myChart.getDataURL({
      type: 'svg',
      pixelRatio: 2,
      backgroundColor: '#fff'
    })
  }
}
